import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { LocalStorageService } from "../services/local-storage/local-storage";
import { history } from "react-router-guard";

/**
 * Token structure interface
 */
interface Token {
  access_token: string;
  [key: string]: any;
}

/**
 * Routes constants
 */
const ROUTES = {
  LOGIN: "/login",
  DASHBOARD: "/dashboard",
} as const;

/**
 * URL patterns that should be excluded from authentication
 */
const EXCLUDED_AUTH_URLS = [
  "token",
  "amazonaws.com",
  "verify-new-user",
  "can-set-password",
  "https://storage",
] as const;

/**
 * URL patterns that should be excluded from language header
 */
const EXCLUDED_LANG_URLS = ["keycloak", "identity"] as const;

/**
 * URL patterns that should be excluded from tenantId parameter
 */
const EXCLUDED_TENANT_URLS = [
  "tenantId",
  "keycloak",
  "me/tenants",
  "login",
  "add-user-to-tenant",
  "add-user",
  "identity",
  "https://storage-",
] as const;

/**
 * URL patterns that should redirect to dashboard instead of login on 401
 */
const DASHBOARD_REDIRECT_URLS = ["cases/"] as const;

/**
 * Default language
 */
const DEFAULT_LANG = "DE";

/**
 * Check if URL matches any of the excluded patterns
 */
const isUrlExcluded = (
  url: string | undefined,
  patterns: readonly string[]
): boolean => {
  if (!url) return false;
  return patterns.some((pattern) => url.includes(pattern));
};

/**
 * Check if request needs authentication
 */
const needsAuthentication = (
  url: string | undefined,
  hasAuth: boolean
): boolean => {
  return !hasAuth && !isUrlExcluded(url, EXCLUDED_AUTH_URLS);
};

/**
 * Check if request needs language header
 */
const needsLanguageHeader = (url: string | undefined): boolean => {
  return !isUrlExcluded(url, EXCLUDED_LANG_URLS);
};

/**
 * Check if request needs tenantId parameter
 */
const needsTenantId = (url: string | undefined): boolean => {
  return !isUrlExcluded(url, EXCLUDED_TENANT_URLS);
};

/**
 * Handle unauthorized errors (401)
 */
const handleUnauthorized = (
  url: string | undefined,
  localStorageService: LocalStorageService
): void => {
  if (url && DASHBOARD_REDIRECT_URLS.some((pattern) => url.includes(pattern))) {
    history.push(ROUTES.DASHBOARD);
  } else {
    localStorageService.clearAll();
    history.push(ROUTES.LOGIN);
  }
};

/**
 * Handle forbidden errors (403)
 */
const handleForbidden = (localStorageService: LocalStorageService): void => {
  localStorageService.clearAll();
  history.push(ROUTES.LOGIN);
};

/**
 * Handle server errors (500, 502, 503, 504)
 */
const handleServerError = (status: number): void => {
  // Log server errors for monitoring
  console.error(`Server error ${status}: Please try again later`);
};

/**
 * Track if interceptors are already set up to prevent duplicate registration
 */
let interceptorsSetup = false;

/**
 * Setup axios interceptors for authentication and error handling
 */
export const setupInterceptors = (): (() => void) | void => {
  // Prevent duplicate interceptor registration
  if (interceptorsSetup) {
    console.warn(
      "Interceptors are already set up. Skipping duplicate registration."
    );
    return;
  }
  const localStorageService: LocalStorageService = new LocalStorageService();

  // Request interceptor
  const requestInterceptor = axios.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      try {
        const url = config.url;
        const hasAuth = !!config.headers.Authorization;

        // Add authentication token
        if (needsAuthentication(url, hasAuth)) {
          const token = localStorageService.getData("token") as Token | null;

          if (token?.access_token) {
            config.headers.Authorization = `Bearer ${token.access_token}`;
          } else {
            console.warn("No valid token found in localStorage");
          }
        }

        // Add language header
        if (needsLanguageHeader(url)) {
          const lang = localStorageService.getData("lang") || DEFAULT_LANG;
          config.headers.lang = lang;
        }

        // Add tenantId parameter
        if (needsTenantId(url)) {
          const tenantId = localStorageService.getData("tenantId");

          if (tenantId) {
            if (config.params) {
              config.params.tenantId = tenantId;
            } else {
              config.params = { tenantId };
            }
          } else {
            console.warn("No tenantId found in localStorage");
          }
        }

        return config;
      } catch (error) {
        console.error("Error in request interceptor:", error);
        return Promise.reject(error);
      }
    },
    (error: AxiosError) => {
      console.error("Request interceptor error:", error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  const responseInterceptor = axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      try {
        // Handle network errors (no response)
        if (!error.response) {
          console.error("Network error:", error.message);
          return Promise.reject({
            message: "Network error. Please check your connection.",
            isNetworkError: true,
          });
        }

        const { status, config } = error.response;
        const url = config?.url;

        // Handle different error status codes
        switch (status) {
          case 401:
            // Unauthorized - token expired or invalid
            handleUnauthorized(url, localStorageService);
            break;

          case 403:
            // Forbidden - user doesn't have permission
            handleForbidden(localStorageService);
            break;

          case 404:
            // Not found
            console.warn(`Resource not found: ${url}`);
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors
            handleServerError(status);
            break;

          default:
            // Other errors
            console.error(`HTTP Error ${status}:`, error.response.data);
        }

        // Return error response data or formatted error
        const errorData = error.response.data || {
          message: error.message || "An error occurred",
          status,
        };

        return Promise.reject(errorData);
      } catch (handlerError) {
        console.error("Error in response interceptor:", handlerError);
        return Promise.reject(error);
      }
    }
  );

  interceptorsSetup = true;

  // Return cleanup function (useful for testing or re-initialization)
  return () => {
    axios.interceptors.request.eject(requestInterceptor);
    axios.interceptors.response.eject(responseInterceptor);
    interceptorsSetup = false;
  };
};

/**
 * Reset interceptors setup flag (useful for testing)
 */
export const resetInterceptorsSetup = (): void => {
  interceptorsSetup = false;
};
